import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import {
  Report,
  ReportFolder,
  ReportNotification,
  ReportNotificationPayloadItem,
  ReportSlackApp,
  TrimmedReport,
} from '@/types/reports';

export const useReportsStore = defineStore('Reports', () => {
  /**
   * Reports list from the v0.4 endpoint.
   * Note: These reports do NOT include columns or filters.
   * Use fetchReportById() to get the full report when columns/filters are needed.
   */
  const reports = ref<TrimmedReport[]>([]);
  const folders = ref<ReportFolder[]>([]);
  const slackApp = ref<ReportSlackApp | null>(null);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const reportResp = await crossbeamApi.GET('/v0.4/reports');
      const folderResp = await crossbeamApi.GET('/v0.1/report-folders');
      reports.value = reportResp.data?.items || [];
      folders.value = folderResp.data || [];
    },
  );

  /* getters */
  function getByFolderId(id: string): ReportFolder | undefined {
    return folders.value.find((folder) => folder.id === id);
  }

  function getByReportId(id: string): TrimmedReport | undefined {
    return reports.value.find((report) => report.id === id);
  }

  function getByReportName(name: string): TrimmedReport | undefined {
    if (!name) return;
    return reports.value.find(
      (report) =>
        report.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
  }

  function getReportsIncludingString(str: string): TrimmedReport[] {
    const normalizedStr = str.trim().toLowerCase();
    return reports.value.filter((report) =>
      report.name.trim().toLowerCase().includes(normalizedStr),
    );
  }

  /* actions */
  async function deleteReport(report: TrimmedReport | Report): Promise<void> {
    reports.value = reports.value.filter(
      (existing) => existing.id !== report?.id,
    );
    await crossbeamApi.DELETE('/v0.2/reports/{uuid}', {
      params: {
        path: { uuid: report.id },
      },
    });
  }

  async function loadSlack(): Promise<void> {
    try {
      const response = await crossbeamApi.GET('/v0.1/slack-app');
      slackApp.value = response.data as ReportSlackApp;
    } catch (err) {
      captureException(err);
    }
  }

  async function saveNotificationConfigs({
    reportId,
    notifications,
  }: {
    reportId: string;
    notifications: ReportNotificationPayloadItem[];
  }): Promise<ReportNotification[] | null> {
    const { data, error } = await crossbeamApi.PUT(
      '/v0.2/reports/{report_id}/notification-configs',
      {
        params: {
          path: { report_id: reportId },
        },
        body: { items: notifications },
      },
    );
    if (error) {
      captureException(error);
      return null;
    }
    const report = reports.value.find((report) => report.id === reportId);
    if (report) report.notification_configs = notifications;
    return data?.items || [];
  }

  function upsertReport(report: Report | TrimmedReport): void {
    // Cast to TrimmedReport since the list doesn't include columns/filters
    reports.value = reports.value
      .filter((existing) => existing.id !== report.id)
      .concat(report as TrimmedReport);
  }

  async function fetchReportById(reportId: string): Promise<Report | null> {
    const { data, error } = await crossbeamApi.GET('/v0.1/reports/{uuid}', {
      params: { path: { uuid: reportId } },
    });

    if (error) {
      captureException(error);
      return null;
    }

    const report = data as Report;
    upsertReport(report);
    return report;
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    deleteReport,
    fetchReportById,
    folders,
    getByFolderId,
    getReportsIncludingString,
    getByReportId,
    getByReportName,
    loadSlack,
    refreshReportsStore: refresh,
    reports,
    saveNotificationConfigs,
    slackApp,
    upsertReport,
  };
});
